import _ from "lodash";
import { toast } from "react-toastify";

import API from "../../api";
import { Report } from "../report";

import { format } from "../../formatResponses/subscriptions";
import { useSubscribers } from "../../../store/subscription/useSubscribers";
import { TRANSACTION_PROCESSING_ERRORS } from "../../../store/subscription/constants";

export class Subscriber {
  setDetails = useSubscribers.getState().actions.setDetails;
  setSubscribers = useSubscribers.getState().actions.setSubscribers;
  setAvailablePlans = useSubscribers.getState().actions.setAvailablePlans;
  setPaginationPage = useSubscribers.getState().actions.setPaginationPage;
  setCycleAppointments = useSubscribers.getState().actions.setCycleAppointments;
  setCycleTransactions = useSubscribers.getState().actions.setCycleTransactions;

  setLoading = useSubscribers.getState().actions.setLoading;
  setWaiting = useSubscribers.getState().actions.setWaiting;
  setProcessing = useSubscribers.getState().actions.setProcessing;

  setFilterData = useSubscribers.getState().actions.setFilter;
  setSummary = useSubscribers.getState().actions.setSummary;

  clearSubscribers = useSubscribers.getState().actions.clearSubscribers;

  constructor() {
    this.name = "subscriptions-subscribers";

    this.dashboardType = "subscribers";
    this.reportName = `${this.name}-report`;

    this.MReport = new Report(this.reportName);


    this.setFilterData({
      summaryType: this.dashboardType,
    });
    this.setReportFilter();
  }

  setFilter(filterProps) {
    this.setFilterData(filterProps);
    this.setReportFilter();
  }

  setReportFilter() {
    const filter = useSubscribers.getState().data.filter;

    this.MReport.setFilter({
      cpf: filter.cpf || '',
      size: filter.size || '',
      status: filter.status || '',
    });
  }

  export() {
    this.MReport.download();
  }

  async setPage(page) {
    const pageExists = useSubscribers.getState().data.subscribers[page];
    this.setPaginationPage(page);

    if (!pageExists) await this.findSubscribers();
  }

  async findSummary() {
    try {
      const { data } = await API.get(`/subscriptions/store/dashboard`, {
        params: this.queryParams()
      });

      this.setSummary({
        totalSubscribers: data.dashboard.totalSubscribers,
        totalActives: data.dashboard.totalActiveSubscribers,
        totalOverdue: data.dashboard.totalOverdueSubscribers,
        totalCanceled: data.dashboard.totalInactiveSubscribers,
      });
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });
      return null;
    }
  }

  async findSubscription(id) {
    try {
      const { data } = await API.get(`subscriptions/store/list/${id}`);

      const formattedSubscriber = format.subscriber(data.subscription);
      this.setDetails(formattedSubscriber)

      return true
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });
      return null;
    }
  }

  async findAvailablePlans(petId) {
    try {
      const { data } = await API.get(`subscriptions/store/checkout/available-plans?petId=${petId}&newPlan=true`);


      const formattedAvailablePlans = format.availablePlans(data.availablePlan);

      this.setAvailablePlans(formattedAvailablePlans)

      return true
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });
      return null;
    }
  }

  async findCycleDetails(id) {
    try {
      const { data } = await API.get(`/subscriptions/store/realized-service/cycle/${id}`);

      const formattedCycles = format.cycles(data.cycleAppointments);
      this.setCycleAppointments(formattedCycles)

      return true
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });
      return false;
    }
  }
  async findCycleTransactions(id) {
    try {
      this.setProcessing(true);
      const seenPixChargeIds = new Set()
      const { data } = await API.get(`/subscriptions/store/cycle/${id}/transactions`);

      const transactions = data?.transactions.filter(item => {
        if (item.payment?.method?.code !== 'pix') return true
        if (seenPixChargeIds.has(item.chargeId)) return false

        seenPixChargeIds.add(item.chargeId)
        return true
      })


      const formattedTransactions = format.transactions(transactions || []);
      this.setCycleTransactions(formattedTransactions)

      this.setProcessing(false);
      return true
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });

      this.setProcessing(false);
      return false;
    }
  }
  async changeCreditCard(subscriptionId, petId) {
    try {
      this.setProcessing(true);
      await API.post(`subscriptions/customer/subscriptions/card/token`, {
        subscriptionId,
        petId,
      });

      this.setProcessing(false);
      return true
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });

      this.setProcessing(false);
      return null;
    }
  }

  async changePlan(subscriptionId, newPlan) {
    try {
      await API.post('subscriptions/change-plan/token/new', {
        subscriptionId,
        newPlan,
      })

      return true
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });
      return null;
    }
  }

  async cancelCreditCard(subscriptionId) {
    try {
      return await API.post(`subscriptions/cancel/${subscriptionId}/token`);

    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });
      return null;
    }
  }
  async pausePlan(subscriptionId) {
    try {
      await API.post(`subscriptions/pause/token/new`, {
        subscriptionId
      });

      return true
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });
      return null;
    }
  }

  async tryRecharge(subscriptionId, cycleId) {
    try {
      this.setProcessing(true);
      const { data } = await API.post(`subscriptions/store/cycle/${cycleId}/try-recharge`, { subscriptionId });
      await this.findCycleTransactions(cycleId)

      this.setProcessing(false);
      return format.transaction(data?.transaction || {});
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });

      this.setProcessing(false);
      return null;
    }
  }

  async changePaymentMethod(subscriptionId, cycleId, method) {
    try {
      this.setProcessing(true);
      const { data } = await API.post(`subscriptions/store/cycle/${cycleId}/new-method`, { subscriptionId, method });
      await this.findCycleTransactions(cycleId)

      this.setProcessing(false);
      return format.transaction(data?.transaction || {});
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });

      this.setProcessing(false);
      return null;
    }
  }

  async refundPayment(cycleId) {
    try {
      this.setProcessing(true);
      const { data } = await API.post(`subscriptions/store/cycle/${cycleId}/refund`);
      await this.findCycleTransactions(cycleId)

      this.setProcessing(false);
      toast.success("Solicitação de estorno enviada!", { theme: "colored" });

      return format.transaction(data?.transaction || {});
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });

      this.setProcessing(false);
      return null;
    }
  }

  async priceAdjustment(adjustmentData) {
    try {
      const response = await API.post(`subscriptions/jobs/create-adjust-queue-from-agenda`, {
        data: adjustmentData
      });

      // Status 201 = Sucesso com "Adicionado"
      if (response.status === 201) {
        // Atualizar os dados da assinatura para trazer priceAdjustment e queueJobs atualizados
        await this.findSubscription(adjustmentData.subscriptionId);
        return { success: true, data: response.data, message: response.data?.message || "Sucesso" }
      } else {
        return { success: false, data: response.data, message: response.data?.message || "Erro ao reajustar" }
      }
    } catch (error) {
      console.error('Erro na API de reajuste:', error);
      // Tratar diferentes tipos de erro
      let errorMessage = "Erro interno do servidor";

      if (error.response?.data?.friendly_message) {
        errorMessage = error?.response?.data?.friendly_message;
      } else if (error.response?.data?.message) {
        errorMessage = error?.response?.data?.message;
      } else if (error.response?.status === 403) {
        errorMessage = "Acesso não autorizado para realizar reajustes";
      } else if (error.response?.status === 400) {
        errorMessage = "Dados inválidos para o reajuste";
      } else if (error.response?.status === 404) {
        errorMessage = "Assinatura não encontrada";
      } else if (error.message) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage }
    }
    finally {
      // Garantir que os dados sejam atualizados mesmo em caso de erro
      try {
        await this.findSubscription(adjustmentData.subscriptionId);
      } catch (updateError) {
        console.warn('Erro ao atualizar dados da assinatura após reajuste:', updateError);
      }
    }
  }

  async findSubscribers() {
    await this.getSubscribers();
  }

  async filterSubscribers() {
    this.setLoading(true);
    this.setPaginationPage(0);
    this.clearSubscribers();
    await this.getSubscribers();
    await this.findSummary();
    this.setLoading(false);
  }

  async getSubscribers() {
    try {
      this.setWaiting(true);

      const { data } = await API.get(`subscriptions/store/list`, {
        params: this.queryParams()
      });

      const formattedSubscribers = format.subscribers(data.storeSubscriptions);
      const pagination = format.pagination(data.pagination);

      this.setSubscribers(formattedSubscribers, pagination);
      this.setWaiting(false);
    } catch (error) {
      toast.error(error?.response?.data?.friendly_message, { theme: "colored" });
      this.setWaiting(false);
      return null;
    }
  }

  queryParams() {
    const filter = useSubscribers.getState().data.filter;
    const pagination = useSubscribers.getState().data.pagination;
    const sort = useSubscribers.getState().data.sort;

    return _.pickBy(
      {
        cpf: filter.cpf || null,
        type: filter.summaryType,
        size: filter.size || null,
        startDate: filter.startDate || null,
        endDate: filter.endDate || null,
        status: filter.status || null,
        skip: pagination.page || 0,
        limit: pagination.perPage || 10,
        sortBy: sort?.sortBy || null,
        sortOrder: sort?.sortOrder || null,
      },
      _.identity
    );
  }

  async setSortAndFetch(sortBy, sortOrder) {
    useSubscribers.getState().actions.setSort(sortBy, sortOrder);
    this.setPaginationPage(0);
    this.clearSubscribers();
    await this.getSubscribers();
    await this.findSummary();
  }
}
